import { useEffect, useState } from 'react';
import { pickerApi } from '@formosa/shared/api/im';
import { useChat } from '../../chat/ChatProvider';

const REACTABLE_TYPES = new Set(['TEXT', 'IMAGE']);
/** fallback when picker not yet loaded */
const FALLBACK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
/** fallback；正式上限由 Nacos im.reaction.max-emojis-per-message 經 picker API 下發 */
const FALLBACK_MAX_REACTION_EMOJIS = 20;

export default function MessageReactions({ conversationId, message }) {
  const { reactToMessage } = useChat();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    if (!pickerOpen || catalog) return;
    let cancelled = false;
    pickerApi
      .reactions()
      .then((data) => {
        if (!cancelled) setCatalog(data || {});
      })
      .catch(() => {
        if (!cancelled) {
          setCatalog({
            quickBar: FALLBACK_EMOJIS.map((emoji) => ({ emoji })),
            maxEmojisPerMessage: FALLBACK_MAX_REACTION_EMOJIS,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [pickerOpen, catalog]);

  if (!message?.serverMessageId || message.status === 'REVOKED') {
    return null;
  }
  if (!REACTABLE_TYPES.has(message.messageType)) {
    return null;
  }

  const reactions = Array.isArray(message.reactions) ? message.reactions : [];
  const existingEmojis = new Set(reactions.map((r) => r.emoji));
  const distinctCount = reactions.length;
  const maxEmojis = resolveMaxEmojis(catalog);
  const limitReached = distinctCount >= maxEmojis;
  const pickList = buildPickList(catalog);

  const onPick = async (emoji, locked) => {
    if (busy || locked || !emoji) return;
    // 跟按既有表情永遠允許；僅「首次引入新表情」受單則上限限制
    if (!existingEmojis.has(emoji) && limitReached) return;
    setBusy(true);
    setPickerOpen(false);
    try {
      await reactToMessage(conversationId, message.serverMessageId, emoji);
    } catch {
      /* WS/history will correct */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="chat-bubble__reactions">
      {reactions.map((r) => (
        <button
          key={r.emoji}
          type="button"
          className={`chat-reaction-chip ${r.reactedByMe ? 'is-mine' : ''}`}
          disabled={busy}
          onClick={() => onPick(r.emoji, false)}
          title={r.reactedByMe ? '取消反應' : '加上相同反應'}
        >
          <span>{r.emoji}</span>
          <span className="chat-reaction-chip__count">{r.count}</span>
        </button>
      ))}
      <div className="chat-reaction-add">
        <button
          type="button"
          className="chat-link-btn"
          disabled={busy}
          onClick={() => setPickerOpen((v) => !v)}
          aria-label="加入反應"
          title={limitReached ? `已達 ${maxEmojis} 種表情上限；仍可跟按既有表情` : '加入反應'}
        >
          +
        </button>
        {distinctCount > 0 ? (
          <span
            className={`chat-reaction-count${limitReached ? ' is-full' : ''}`}
            title="此訊息目前的表情種類數／上限"
          >
            {distinctCount}/{maxEmojis}
          </span>
        ) : null}
        {pickerOpen ? (
          <div className="chat-reaction-picker" role="listbox">
            {pickList.map((item) => {
              const joinable = existingEmojis.has(item.emoji);
              const blockedByLimit = limitReached && !joinable;
              const disabled = busy || item.locked || blockedByLimit;
              const title = item.locked
                ? '未解鎖'
                : blockedByLimit
                ? `已達 ${maxEmojis} 種表情上限`
                : item.name || item.emoji;
              return (
                <button
                  key={`${item.packId || 'x'}-${item.emoji}`}
                  type="button"
                  className={`chat-reaction-picker__btn${item.locked ? ' is-locked' : ''}${
                    blockedByLimit ? ' is-blocked' : ''
                  }`}
                  disabled={disabled}
                  title={title}
                  onClick={() => onPick(item.emoji, item.locked)}
                >
                  {item.emoji}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function resolveMaxEmojis(catalog) {
  const n = Number(catalog?.maxEmojisPerMessage);
  if (Number.isFinite(n) && n >= 1) return Math.floor(n);
  return FALLBACK_MAX_REACTION_EMOJIS;
}

function buildPickList(catalog) {
  if (!catalog) {
    return FALLBACK_EMOJIS.map((emoji) => ({ emoji, locked: false }));
  }
  const seen = new Set();
  const out = [];
  const push = (item, locked = false) => {
    if (!item?.emoji || seen.has(item.emoji)) return;
    seen.add(item.emoji);
    out.push({ ...item, locked: !!locked });
  };
  (catalog.quickBar || []).forEach((i) => push(i, false));
  (catalog.recent || []).forEach((i) => push(i, false));
  (catalog.packs || []).forEach((pack) => {
    (pack.items || []).forEach((i) => push(i, pack.locked));
  });
  if (!out.length) {
    return FALLBACK_EMOJIS.map((emoji) => ({ emoji, locked: false }));
  }
  return out;
}
